import { Controller } from '@hotwired/stimulus';
import 'chart.js/dist/chart.min';

export default class extends Controller {
  static values = { projects: Object, categories: Object }
  static targets = ['canvas', 'legend', 'projectsBtn', 'categoriesBtn']

  connect() {
    this.currentView = 'projects'
    const emptyEl = this.legendTarget.querySelector('.text-muted')
    this.emptyText = emptyEl ? emptyEl.textContent : null
    this.initChart(this.projectsValue)
  }

  showProjects() {
    this.currentView = 'projects'
    this.updateChart(this.projectsValue)
    this.updateButtons('projects')
  }

  showCategories() {
    this.currentView = 'categories'
    this.updateChart(this.categoriesValue)
    this.updateButtons('categories')
  }

  initChart(data) {
    this.currentData = data
    const isEmpty = !data.details || data.details.length === 0
    const self = this
    const config = {
      type: 'doughnut',
      data: data,
      options: {
        cutout: '60%',
        layout: { padding: 4 },
        events: isEmpty ? [] : ['mousemove', 'mouseout', 'click'],
        plugins: {
          tooltip: {
            enabled: false,
            external: isEmpty ? undefined : (ctx) => self.externalTooltip(ctx),
          },
          legend: { display: false }
        }
      }
    }
    this.chart = new Chart(this.canvasTarget, config)
    this.buildLegend(data)
  }

  updateChart(data) {
    this.currentData = data
    const isEmpty = !data.details || data.details.length === 0
    const self = this
    this.chart.data = data
    this.chart.options.events = isEmpty
      ? []
      : ['mousemove', 'mouseout', 'click']
    this.chart.options.plugins.tooltip.external = isEmpty
      ? undefined
      : (ctx) => self.externalTooltip(ctx)
    this.chart.update()
    this.buildLegend(data)
  }

  externalTooltip(context) {
    let el = this.element.querySelector('.chart-tooltip')
    if (!el) {
      el = document.createElement('div')
      el.classList.add('chart-tooltip')
      el.style.position = 'absolute'
      el.style.background = '#1f2937'
      el.style.color = '#fff'
      el.style.borderRadius = '6px'
      el.style.padding = '6px 12px'
      el.style.fontSize = '13px'
      el.style.pointerEvents = 'none'
      el.style.zIndex = '10'
      el.style.whiteSpace = 'nowrap'
      el.style.transition = 'opacity 0.15s'
      this.element.style.position = 'relative'
      this.element.appendChild(el)
    }

    const tooltip = context.tooltip
    if (tooltip.opacity === 0) {
      el.style.opacity = '0'
      return
    }

    const details = this.currentData.details[tooltip.dataPoints[0].dataIndex]
    if (!details) {
      el.style.opacity = '0'
      return
    }

    const amount = new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(details.amount)
    const pct = details.percentage.toFixed(1).replace('.', ',')
    const idx = tooltip.dataPoints[0].dataIndex
    const color = this.currentData.datasets[0].backgroundColor[idx]
    const dotStyle = [
      'display:inline-block',
      'width:10px',
      'height:10px',
      'border-radius:2px',
      `background:${color}`,
      'border:1px solid #fff',
      'margin-right:6px',
      'vertical-align:baseline',
    ].join(';')
    el.innerHTML = `<strong>${details.name}</strong><br>`
      + `<span style="${dotStyle}"></span>${pct}% - ${amount}`

    const canvasRect = this.canvasTarget.getBoundingClientRect()
    const cardRect = this.element.getBoundingClientRect()
    const left = canvasRect.left - cardRect.left + tooltip.caretX
    const top = canvasRect.top - cardRect.top + tooltip.caretY

    el.style.opacity = '1'
    el.style.left = `${left}px`
    el.style.top = `${top}px`
    el.style.transform = 'translate(-50%, -120%)'
  }

  updateButtons(active) {
    const activeClasses = ['bg-primary', 'text-white', 'rounded-1']
    const inactiveClasses = ['text-dark']

    if (active === 'projects') {
      this.setActive(this.projectsBtnTarget, activeClasses, inactiveClasses)
      this.setActive(this.categoriesBtnTarget, inactiveClasses, activeClasses)
    } else {
      this.setActive(this.categoriesBtnTarget, activeClasses, inactiveClasses)
      this.setActive(this.projectsBtnTarget, inactiveClasses, activeClasses)
    }
  }

  setActive(btn, addClasses, removeClasses) {
    btn.classList.remove(...removeClasses)
    btn.classList.add(...addClasses)
  }

  buildLegend(data) {
    const container = this.legendTarget
    container.innerHTML = ''

    const details = data.details
    if (!details || details.length === 0) {
      if (this.emptyText) {
        const p = document.createElement('p')
        p.className = 'text-muted small mb-0'
        p.textContent = this.emptyText
        container.appendChild(p)
      }
      return
    }

    const colors = data.datasets[0].backgroundColor

    const ul = document.createElement('ul')
    ul.style.listStyle = 'none'
    ul.style.padding = '0'
    ul.style.margin = '0'

    details.forEach((item, idx) => {
      const li = document.createElement('li')
      li.className = 'small'
      li.style.display = 'flex'
      li.style.alignItems = 'center'
      li.style.marginBottom = '8px'

      const dot = document.createElement('span')
      dot.style.display = 'inline-block'
      dot.style.width = '10px'
      dot.style.height = '10px'
      dot.style.borderRadius = '50%'
      dot.style.background = colors[idx] || '#E5E7EB'
      dot.style.marginRight = '8px'
      dot.style.flexShrink = '0'

      const text = document.createElement('span')
      text.textContent = item.name
      text.style.overflow = 'hidden'
      text.style.textOverflow = 'ellipsis'
      text.style.whiteSpace = 'nowrap'

      li.appendChild(dot)
      li.appendChild(text)
      ul.appendChild(li)
    })

    container.appendChild(ul)
  }
}
